import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalPopup, TextArea, AlertPopup } from "../../common";
import SuccessIcon from "../../common/SuccessIcon";
import FeedbackRequestDto from "../../../types/feedback/FeedbackRequestDto";
import feedbackRequestApi from "../../../api/feedback/feedbackRequestApi";
import { MultipleChoiceOptionStringId } from "../../../types/forms/MultipleChoiceOptions";
import { buildDeclineOptions } from "./shared/feedbackDeclineOptions";
import {
  FeedbackDeclineStepBody,
  FeedbackDeclineStepFooter,
} from "./shared/FeedbackDeclineStep";
import FeedbackRequestMessageQuote from "./shared/FeedbackRequestMessageQuote";
import useFeedbackDeclineSubmit from "./shared/useFeedbackDeclineSubmit";
import { formatPendingRequestSummary } from "./shared/formatPendingRequestSummary";
import { normalizeFeedbackRequestError } from "./shared/normalizeFeedbackRequestError";

const FEEDBACK_SUCCESS_AUTO_CLOSE_SECONDS = 3;

export type DashboardLeaveFeedbackStep =
  | "intro"
  | "compose"
  | "decline"
  | "success";

interface DashboardLeaveFeedbackModalProps {
  isOpen: boolean;
  request: FeedbackRequestDto | null;
  api: feedbackRequestApi;
  onClose: () => void;
  onRequestCompleted: () => void;
  onSaveError: (title: string, message: string) => void;
}

function DashboardLeaveFeedbackModal({
  isOpen,
  request,
  api,
  onClose,
  onRequestCompleted,
  onSaveError,
}: DashboardLeaveFeedbackModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<DashboardLeaveFeedbackStep>("intro");
  const [noteBody, setNoteBody] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [successCloseCountdown, setSuccessCloseCountdown] = useState(
    FEEDBACK_SUCCESS_AUTO_CLOSE_SECONDS
  );
  const [declineOptions, setDeclineOptions] = useState<
    MultipleChoiceOptionStringId[]
  >(() => buildDeclineOptions(t));

  const resetFormFields = useCallback(() => {
    setStep("intro");
    setNoteBody("");
    setIsSavingNote(false);
    setShowDiscardConfirm(false);
    setSuccessCloseCountdown(FEEDBACK_SUCCESS_AUTO_CLOSE_SECONDS);
    setDeclineOptions(buildDeclineOptions(t));
  }, [t]);

  const finishAndClose = useCallback(() => {
    resetFormFields();
    onClose();
  }, [onClose, resetFormFields]);

  const finishSuccess = useCallback(() => {
    finishAndClose();
    onRequestCompleted();
  }, [finishAndClose, onRequestCompleted]);

  const { isSaving: isSavingDecline, saveError, submitDecline, clearSaveError } =
    useFeedbackDeclineSubmit({
      api,
      requestId: request?.id,
      onSaved: () => {
        finishAndClose();
        onRequestCompleted();
      },
      onWillGive: () => {
        clearSaveError();
        setStep("compose");
      },
    });

  const resetState = useCallback(() => {
    resetFormFields();
    clearSaveError();
  }, [clearSaveError, resetFormFields]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    if (step !== "success") return;

    if (successCloseCountdown <= 0) {
      finishSuccess();
      return;
    }

    const timer = window.setTimeout(() => {
      setSuccessCloseCountdown((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [step, successCloseCountdown, finishSuccess]);

  const isSaving = isSavingNote || isSavingDecline;
  const requestMessage = request?.message?.trim() ?? "";

  const requestClose = () => {
    if (isSaving || step === "success") return;
    if (step === "compose" && noteBody.trim().length > 0) {
      setShowDiscardConfirm(true);
      return;
    }
    finishAndClose();
  };

  const submitNote = () => {
    if (!request || !noteBody.trim()) return;
    setIsSavingNote(true);

    api.saveNote(
      request.id,
      { body: noteBody.trim() },
      () => {
        setIsSavingNote(false);
        setStep("success");
        setSuccessCloseCountdown(FEEDBACK_SUCCESS_AUTO_CLOSE_SECONDS);
      },
      (err: unknown) => {
        setIsSavingNote(false);
        const fallback = t("Feedback.Dashboard.NoteSaveFailedMessage", {
          defaultValue: "Something went wrong. Please try again.",
        });
        onSaveError(
          t("Feedback.Dashboard.NoteSaveFailedTitle", {
            defaultValue: "Couldn't save feedback",
          }),
          normalizeFeedbackRequestError(err, fallback)
        );
      }
    );
  };

  const goToDeclineStep = () => {
    setDeclineOptions(buildDeclineOptions(t));
    clearSaveError();
    setStep("decline");
  };

  const modalWidth = step === "compose" ? "SMALL" : "TINY";

  const modalTitle =
    step === "success"
      ? null
      : t("Feedback.Dashboard.LeaveFeedbackIntro.Title", {
          defaultValue: "Give your feedback",
        });

  const titleDescription =
    step === "intro" && request
      ? formatPendingRequestSummary(request, t)
      : undefined;

  let primaryButtonText: string | undefined;
  let onPrimaryClick: (() => void) | undefined;
  let secondaryButtonText: string | undefined;
  let onSecondaryClick: (() => void) | undefined;
  let isPrimaryDisabled = false;
  let footerComponent: JSX.Element | undefined;

  if (step === "success") {
    primaryButtonText = t("Feedback.Dashboard.SuccessCloseCountdown", {
      count: successCloseCountdown,
      defaultValue: "Close ({{count}})",
    });
    onPrimaryClick = finishSuccess;
  } else if (step === "intro") {
    primaryButtonText = t("Feedback.Dashboard.LeaveFeedbackIntro.GiveFeedbackCta", {
      defaultValue: "Give feedback",
    });
    onPrimaryClick = () => setStep("compose");
    secondaryButtonText = t("Feedback.RecipientIntro.DeclineCta", {
      defaultValue: "I'd prefer not to give feedback",
    });
    onSecondaryClick = goToDeclineStep;
  } else if (step === "compose") {
    primaryButtonText = isSavingNote
      ? t("Feedback.Dashboard.NoteSubmitting", {
          defaultValue: "Submitting…",
        })
      : t("Common.Submit", { defaultValue: "Submit" });
    onPrimaryClick = submitNote;
    isPrimaryDisabled = !noteBody.trim() || isSavingNote;
    secondaryButtonText = t("Common.Back", { defaultValue: "Back" });
    onSecondaryClick = () => setStep("intro");
  } else if (step === "decline") {
    footerComponent = (
      <FeedbackDeclineStepFooter
        declineOptions={declineOptions}
        onBack={() => {
          setStep("intro");
          clearSaveError();
        }}
        onSubmit={() => submitDecline(declineOptions)}
        isSaving={isSavingDecline}
      />
    );
  }

  return (
    <>
      <ModalPopup
        isOpen={isOpen && !showDiscardConfirm}
        onOpenChange={(open) => {
          if (!open) {
            if (step === "success") {
              finishSuccess();
            } else {
              requestClose();
            }
          }
        }}
        title={modalTitle}
        titleDescription={titleDescription}
        onPrimaryButtonClick={onPrimaryClick}
        primaryButtonText={primaryButtonText}
        onSecondaryButtonClick={onSecondaryClick}
        secondaryButtonText={secondaryButtonText}
        isPrimaryButtonDisabled={isPrimaryDisabled}
        isLoading={isSaving}
        width={modalWidth}
        showCloseIcon={!isSaving && step !== "success"}
        onAlternativeOpenChange={
          step === "success" ? finishSuccess : requestClose
        }
        footerComponent={footerComponent}
      >
        {step === "success" ? (
          <div className="text-center py-2">
            <SuccessIcon darkMode fromModal />
            <h2 className="text-xl font-semibold text-gray-800 mt-4 mb-2">
              {t("Feedback.Dashboard.NoteSavedTitle", {
                defaultValue: "Feedback sent",
              })}
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {t("Feedback.Dashboard.NoteSavedThankYou", {
                requesterName:
                  request?.requesterName ??
                  t("Common.Someone", { defaultValue: "Someone" }),
                defaultValue:
                  "Thank you for sharing your feedback. {{requesterName}} has been notified.",
              })}
            </p>
          </div>
        ) : step === "intro" ? (
          <div className="space-y-3">
            {requestMessage && (
              <FeedbackRequestMessageQuote
                message={requestMessage}
                ariaLabelKey="Feedback.Dashboard.RequestMessageAria"
              />
            )}
            <p className="text-sm text-gray-600 leading-relaxed">
              {t("Feedback.Dashboard.LeaveFeedbackIntro.InstructionsBody", {
                defaultValue:
                  "On the next step, you can share anything you think would be helpful, then submit your response when you are ready.",
              })}
            </p>
          </div>
        ) : step === "compose" ? (
          <div className="space-y-3">
            {requestMessage && (
              <FeedbackRequestMessageQuote
                message={requestMessage}
                ariaLabelKey="Feedback.Dashboard.RequestMessageAria"
              />
            )}
            <TextArea
              value={noteBody}
              onChange={setNoteBody}
              placeholder={t("Feedback.Dashboard.NotePlaceholder", {
                defaultValue: "Write your feedback…",
              })}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:border-gray-400"
              minRows={6}
              maxRows={12}
              maxLength={4000}
              isReadOnly={isSavingNote}
            />
            <p className="text-xs text-gray-500 leading-relaxed">
              {t("Feedback.Dashboard.NoteSubmitReminder", {
                defaultValue:
                  "Please check your feedback before you submit. You won't be able to change it afterwards.",
              })}
            </p>
          </div>
        ) : (
          <FeedbackDeclineStepBody
            declineOptions={declineOptions}
            onChange={setDeclineOptions}
            saveError={saveError}
            uniqueFieldName="feedback-decline-reason-dashboard"
          />
        )}
      </ModalPopup>

      <AlertPopup
        isOpen={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title={t("Feedback.Dashboard.DiscardNoteTitle", {
          defaultValue: "Discard your feedback?",
        })}
        bodyText={t("Feedback.Dashboard.DiscardNoteBody", {
          defaultValue:
            "You have started writing feedback that has not been submitted. If you close now, what you have written will be lost.",
        })}
        primaryButtonText={t("Feedback.Dashboard.DiscardNoteConfirm", {
          defaultValue: "Discard",
        })}
        onPrimaryButtonClick={() => {
          setShowDiscardConfirm(false);
          finishAndClose();
        }}
        secondaryButtonText={t("Feedback.Dashboard.DiscardNoteCancel", {
          defaultValue: "Keep editing",
        })}
        onSecondaryButtonClick={() => setShowDiscardConfirm(false)}
      />
    </>
  );
}

export default DashboardLeaveFeedbackModal;
